/**
 * mega-menu-fix.js — UrbanTrend
 * v5 — 2026-05-02
 *
 * WHAT THIS FILE FIXES:
 *
 * ROOT CAUSE 1: .ut-nav has backdrop-filter: blur(20px) which creates a new
 * CSS stacking context per the spec. Any position:fixed child of a stacking-
 * context ancestor has its z-index evaluated WITHIN that ancestor — so even at
 * z-index:9150, .ut-mega only beats elements inside the nav, not PDP root-level
 * elements like .ut-gal (position:sticky).
 * → FIX: Hoist every .ut-mega to a direct <body> child (root stacking context).
 *
 * ROOT CAUSE 2: body::after is already owned by urban-trend.css / ut-bundle.css
 * for the grain/noise texture at z-index:9999. Our previous CSS tried to use
 * body::after for the dim backdrop, causing a conflict (CSS last-write-wins —
 * only one ::after per element). The grain texture's rule was later in cascade.
 * → FIX: Inject a real <div id="ut-mega-backdrop"> as a direct body child.
 *   CSS for this div is in mega-menu-fix.css (no ::after needed).
 *
 * ROOT CAUSE 3: .ut-sticky-mob (PDP sticky mobile ATC bar) had z-index:9999
 * in ut-pdp-cta-aplus.css, higher than mega panels at 9150.
 * → FIX: CSS lowers .ut-sticky-mob to z-index:8900. On mobile the mega is
 *   hidden anyway (display:none at <900px), so belt-and-suspenders.
 *
 * PRESERVED FROM v4:
 * FIX A: Scroll threshold 8px
 * FIX B: Cart drawer stub
 * FIX C: Double-init guard
 */

(function () {
  'use strict';

  var raf = window.requestAnimationFrame || function (fn) { setTimeout(fn, 16); };

  function getNavBottom() {
    var nav = document.getElementById('utNav');
    if (!nav) return 88;
    return nav.getBoundingClientRect().bottom;
  }

  /* ── Inject backdrop div (replaces body::after which is taken by grain texture) ── */
  function createBackdrop() {
    var existing = document.getElementById('ut-mega-backdrop');
    if (existing) return existing;
    var div = document.createElement('div');
    div.id = 'ut-mega-backdrop';
    document.body.appendChild(div);
    return div;
  }

  /* ── Core: hoist mega panels to <body> ── */
  function hoistMegaPanels() {
    var nav = document.getElementById('utNav');
    if (!nav) return;

    var backdrop = createBackdrop();
    var items = Array.prototype.slice.call(nav.querySelectorAll('.ut-item'));
    var panels = [];

    items.forEach(function (item, idx) {
      var mega = item.querySelector('.ut-mega');
      if (!mega) return;

      if (!item.id) item.id = 'ut-item-' + idx;
      mega.setAttribute('data-owner', item.id);

      /* Move panel to body — escapes the nav's stacking context */
      document.body.appendChild(mega);

      /* Inline styles: position + z-index + hidden state */
      mega.style.cssText = [
        'position:fixed',
        'left:0',
        'right:0',
        'width:100%',
        'z-index:9150',
        'opacity:0',
        'visibility:hidden',
        'transform:translateY(-8px)',
        'transition:opacity 0.2s ease,transform 0.2s ease,visibility 0s linear 0.2s',
        'pointer-events:none'
      ].join(';');

      mega.style.top = getNavBottom() + 'px';

      panels.push({ item: item, mega: mega });
    });

    if (!panels.length) return;

    var openTimer = null;
    var closeTimer = null;
    var current = null;

    function show(p) {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (current && current !== p) hide(current);
      current = p;

      p.mega.style.top = getNavBottom() + 'px';
      p.item.classList.add('mega-open');
      document.body.classList.add('mega-is-open');
      document.documentElement.style.overflow = 'hidden';

      p.mega.style.opacity = '1';
      p.mega.style.visibility = 'visible';
      p.mega.style.transform = 'translateY(0)';
      p.mega.style.transition = 'opacity 0.2s ease,transform 0.2s ease';
      p.mega.style.pointerEvents = 'all';
    }

    function hide(p) {
      if (!p) return;
      p.item.classList.remove('mega-open');
      p.mega.style.opacity = '0';
      p.mega.style.visibility = 'hidden';
      p.mega.style.transform = 'translateY(-8px)';
      p.mega.style.transition = 'opacity 0.2s ease,transform 0.2s ease,visibility 0s linear 0.2s';
      p.mega.style.pointerEvents = 'none';
      if (current === p) {
        current = null;
        document.body.classList.remove('mega-is-open');
        document.documentElement.style.overflow = '';
      }
    }

    panels.forEach(function (p) {
      /* ── .ut-item hover ── */
      p.item.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        openTimer = setTimeout(function () { show(p); }, 50);
      });
      p.item.addEventListener('mouseleave', function () {
        clearTimeout(openTimer);
        closeTimer = setTimeout(function () { hide(p); }, 100);
      });

      /* ── mega panel hover bridge ── */
      p.mega.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        p.item.classList.add('mega-open');
      });
      p.mega.addEventListener('mouseleave', function () {
        clearTimeout(openTimer);
        closeTimer = setTimeout(function () { hide(p); }, 100);
      });

      /* ── Keyboard focus ── */
      p.item.addEventListener('focusin', function () { show(p); });
      p.item.addEventListener('focusout', function () {
        raf(function () {
          if (!p.item.contains(document.activeElement) && !p.mega.contains(document.activeElement)) {
            hide(p);
          }
        });
      });

      /* ── Touch / click on link ── */
      var link = p.item.querySelector('.ut-link');
      if (link) {
        link.addEventListener('click', function (e) {
          if (p.item.classList.contains('mega-open')) {
            hide(p);
          } else {
            e.preventDefault();
            show(p);
          }
        });
      }
    });

    /* ── Click outside → close ── */
    document.addEventListener('click', function (e) {
      if (!current) return;
      if (current.item.contains(e.target) || current.mega.contains(e.target)) return;
      hide(current);
    }, true);

    /* ── Escape → close ── */
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.keyCode === 27) && current) hide(current);
    });

    /* ── Reposition on scroll ── */
    window.addEventListener('scroll', function () {
      if (!current) return;
      raf(function () {
        if (current) current.mega.style.top = getNavBottom() + 'px';
      });
    }, { passive: true });

    /* ── Reposition on resize ── */
    window.addEventListener('resize', function () {
      panels.forEach(function (p) {
        p.mega.style.top = getNavBottom() + 'px';
      });
    });
  }

  /* ── FIX A: Unified scroll threshold 8px ── */
  function setupScroll(nav) {
    var rafId = null;
    function apply() {
      nav.classList.toggle('scrolled', window.scrollY > 8);
      rafId = null;
    }
    window.addEventListener('scroll', function () {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(apply);
    }, { passive: true });
    apply();
  }

  /* ── Main entry ── */
  window.addEventListener('DOMContentLoaded', function () {
    var nav = document.getElementById('utNav');
    if (!nav) return;

    /* FIX A */
    setupScroll(nav);

    /* FIX B: Cart drawer stub */
    if (typeof window.openCartDrawer !== 'function') {
      window.openCartDrawer = function () {
        if (window.UT && typeof window.UT.openCartDrawer === 'function') {
          window.UT.openCartDrawer();
        }
      };
    }

    /* FIX C: Double-init guard */
    if (window.UT && typeof window.UT.init === 'function' && !window.UT._initialised) {
      window.UT._initialised = true;
      try { window.UT.init(); } catch (e) { /* non-critical */ }
    }

    /* CORE FIX v5: hoist mega panels + inject real backdrop div */
    hoistMegaPanels();
  });

})();
